import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { DOCUMENT } from '../../constants/documentTheme';
import { partnerDangerLinkSx, partnerLinkSx, partnerOxbloodBtnSx } from '../PartnerListChrome';
import { formatMoney } from '../../utils/partnerFinance';
import { COLLECTION_METHODS, TRAVELER_TYPES, isHttpsUrl } from '../../utils/groupBilling';
import { BillingStatusChip, CollectionMethodTiles, OutlineChip, eyebrowSx, fieldSx } from './BillingParts';

function InstallmentOverride({ installment, onSave, onCancel }) {
  const { t } = useTranslation();
  const [method, setMethod] = useState(installment.overrideMethod || '');
  const [url, setUrl] = useState(installment.overrideLinkUrl || '');
  const [label, setLabel] = useState(installment.paymentLinkLabel || '');
  const invalid = method === 'external_link' && !isHttpsUrl(url);
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 2fr 1fr auto' }, gap: 1, mt: 1, alignItems: 'center' }}>
      <Select size="small" displayEmpty value={method} onChange={(e) => setMethod(e.target.value)} sx={{ borderRadius: 0, fontSize: 13 }}>
        <MenuItem value="">{t('groupBilling.account.useInvoiceDefault')}</MenuItem>
        {COLLECTION_METHODS.map((m) => <MenuItem key={m} value={m}>{t(`groupBilling.collection.${m}`)}</MenuItem>)}
      </Select>
      {method === 'external_link' ? (
        <>
          <TextField size="small" sx={fieldSx} placeholder="https://" value={url} onChange={(e) => setUrl(e.target.value)}
            error={Boolean(url) && invalid} inputProps={{ 'data-testid': `group-installment-link-${installment.id}` }} />
          <TextField size="small" sx={fieldSx} placeholder={t('groupBilling.invoice.linkLabel')} value={label}
            onChange={(e) => setLabel(e.target.value)} inputProps={{ maxLength: 80 }} />
        </>
      ) : <><span /><span /></>}
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button size="small" sx={partnerLinkSx} onClick={onCancel}>{t('common.cancel')}</Button>
        <Button
          size="small"
          sx={partnerLinkSx}
          disabled={invalid}
          data-testid={`group-installment-save-${installment.id}`}
          onClick={() => onSave({
            collectionMethod: method || null,
            paymentLinkUrl: method === 'external_link' ? url.trim() : null,
            paymentLinkLabel: method === 'external_link' ? (label.trim() || null) : null,
          })}
        >
          {t('common.save')}
        </Button>
      </Box>
    </Box>
  );
}

const GroupAccountDialog = ({
  open,
  onClose,
  account,
  accounts = [],
  travelers = [],
  currency = 'USD',
  call,
  confirm,
  onSendInvoice,
  onRecordPayment,
  onDownloadPdf,
}) => {
  const { t } = useTranslation();
  const [form, setForm] = useState(null);
  const [editingInstallment, setEditingInstallment] = useState(null);
  const [refund, setRefund] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open || !account) return;
    setForm({
      name: account.name || '',
      email: account.email || '',
      notes: account.notes || '',
      collectionMethod: account.collectionMethod || '',
      paymentLinkUrl: account.paymentLinkUrl || '',
      paymentLinkLabel: account.paymentLinkLabel || '',
    });
    setEditingInstallment(null);
    setRefund(null);
    setError('');
  }, [open, account]);

  if (!account || !form) return null;
  const patch = (next) => setForm((prev) => ({ ...prev, ...next }));
  const members = travelers.filter((tr) => tr.accountId === account.id);
  const linkInvalid = form.collectionMethod === 'external_link' && !isHttpsUrl(form.paymentLinkUrl);

  const act = async (method, url, body, okMessage) => {
    setError('');
    const res = await call(method, url, body, okMessage);
    if (res?.error) setError(res.error);
    return res;
  };

  const saveAccount = () => act('put', `/accounts/${account.id}`, {
    name: form.name,
    email: form.email || null,
    notes: form.notes || null,
    collectionMethod: form.collectionMethod || null,
    paymentLinkUrl: form.collectionMethod === 'external_link' ? form.paymentLinkUrl.trim() : null,
    paymentLinkLabel: form.collectionMethod === 'external_link' ? (form.paymentLinkLabel.trim() || null) : null,
  }, t('groupBilling.notices.saved'));

  const deleteAccount = async () => {
    if (!(await confirm(t('groupBilling.account.deleteConfirm', { name: account.name })))) return;
    const res = await act('delete', `/accounts/${account.id}`);
    if (!res?.error) onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth data-testid="group-account-dialog">
      <DialogTitle sx={{ fontFamily: DOCUMENT.display, borderBottom: `1px solid ${DOCUMENT.rule}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
        <span>{account.name}</span>
        <BillingStatusChip status={account.status} />
      </DialogTitle>
      <DialogContent sx={{ pt: '20px !important' }}>
        {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
        <Stack spacing={3}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(4, 1fr)' }, gap: 2 }}>
            {[
              ['total', account.total], ['invoiced', account.invoiced], ['paid', account.paid], ['balance', account.balance],
            ].map(([key, value]) => (
              <Box key={key}>
                <Typography sx={eyebrowSx}>{t(`groupBilling.account.${key}`)}</Typography>
                <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: 20 }}>{formatMoney(value, currency)}</Typography>
              </Box>
            ))}
          </Box>

          <Box>
            <Typography sx={{ ...eyebrowSx, mb: 1 }}>{t('groupBilling.account.details')}</Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 2 }}>
              <TextField size="small" sx={fieldSx} label={t('groupBilling.account.name')} value={form.name}
                onChange={(e) => patch({ name: e.target.value })} inputProps={{ 'data-testid': 'group-account-name' }} />
              <TextField size="small" sx={fieldSx} label={t('groupBilling.account.email')} value={form.email}
                onChange={(e) => patch({ email: e.target.value })} helperText={t('groupBilling.account.emailHint')} />
            </Box>
            <FormControlLabel
              sx={{ mb: 1 }}
              control={(
                <Checkbox
                  checked={!form.collectionMethod}
                  onChange={(e) => patch({ collectionMethod: e.target.checked ? '' : (account.effectiveCollection?.method || 'in_app') })}
                />
              )}
              label={t('groupBilling.account.useTripDefault')}
            />
            {form.collectionMethod ? (
              <Stack spacing={1.5}>
                <CollectionMethodTiles compact value={form.collectionMethod} onChange={(v) => patch({ collectionMethod: v })} testIdPrefix="group-account-method" />
                {form.collectionMethod === 'external_link' && (
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '2fr 1fr' }, gap: 2 }}>
                    <TextField size="small" sx={fieldSx} label={t('groupBilling.invoice.linkUrl')} placeholder="https://"
                      value={form.paymentLinkUrl} onChange={(e) => patch({ paymentLinkUrl: e.target.value })}
                      error={Boolean(form.paymentLinkUrl) && linkInvalid} />
                    <TextField size="small" sx={fieldSx} label={t('groupBilling.invoice.linkLabel')}
                      value={form.paymentLinkLabel} onChange={(e) => patch({ paymentLinkLabel: e.target.value })} />
                  </Box>
                )}
              </Stack>
            ) : null}
            <TextField size="small" sx={{ ...fieldSx, mt: 2 }} fullWidth multiline minRows={2} label={t('groupBilling.account.notes')}
              value={form.notes} onChange={(e) => patch({ notes: e.target.value })} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1.5 }}>
              <Button size="small" sx={partnerDangerLinkSx} onClick={deleteAccount} data-testid="group-account-delete">
                {t('groupBilling.account.delete')}
              </Button>
              <Button size="small" sx={partnerOxbloodBtnSx} onClick={saveAccount} disabled={!form.name.trim() || linkInvalid} data-testid="group-account-save">
                {t('common.save')}
              </Button>
            </Box>
          </Box>

          <Box>
            <Typography sx={{ ...eyebrowSx, mb: 1 }}>{t('groupBilling.account.travelers')}</Typography>
            {!members.length && <Typography sx={{ fontSize: 13, color: DOCUMENT.ink3 }}>{t('groupBilling.account.noTravelers')}</Typography>}
            {members.map((tr) => (
              <Box key={tr.id} sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '2fr 1fr 1fr 1.5fr' }, gap: 1.5, alignItems: 'center', py: 0.75, borderBottom: `1px solid ${DOCUMENT.rule}` }}>
                <Typography sx={{ fontSize: 14 }}>{tr.name} · {formatMoney(tr.share, currency)}</Typography>
                <Select size="small" value={tr.travelerType} sx={{ borderRadius: 0, fontSize: 13 }}
                  onChange={(e) => act('put', `/travelers/${tr.id}`, { travelerType: e.target.value })}>
                  {TRAVELER_TYPES.map((tt) => <MenuItem key={tt} value={tt}>{t(`groupBilling.travelerType.${tt}`)}</MenuItem>)}
                </Select>
                <FormControlLabel
                  control={<Checkbox size="small" checked={Boolean(tr.isLead)} onChange={(e) => act('put', `/travelers/${tr.id}`, { isLead: e.target.checked })} />}
                  label={<Typography sx={{ fontSize: 13 }}>{t('groupBilling.account.lead')}</Typography>}
                />
                <Select size="small" value={account.id} sx={{ borderRadius: 0, fontSize: 13 }}
                  inputProps={{ 'data-testid': `group-traveler-move-${tr.id}` }}
                  onChange={(e) => act('put', `/travelers/${tr.id}`, { accountId: e.target.value }, t('groupBilling.notices.moved'))}>
                  {accounts.map((a) => (
                    <MenuItem key={a.id} value={a.id}>
                      {a.id === account.id ? t('groupBilling.account.paysSelf', { name: a.name }) : t('groupBilling.account.moveTo', { name: a.name })}
                    </MenuItem>
                  ))}
                </Select>
              </Box>
            ))}
          </Box>

          <Box>
            <Typography sx={{ ...eyebrowSx, mb: 1 }}>{t('groupBilling.account.invoices')}</Typography>
            {!account.invoices.length && <Typography sx={{ fontSize: 13, color: DOCUMENT.ink3 }}>{t('groupBilling.account.noInvoices')}</Typography>}
            {account.invoices.map((inv) => (
              <Box key={inv.id} sx={{ border: `1px solid ${DOCUMENT.rule}`, p: 1.5, mb: 1.5, opacity: inv.status === 'void' ? 0.6 : 1 }} data-testid={`group-invoice-${inv.id}`}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                    <Typography sx={{ fontWeight: 600 }}>{inv.number}</Typography>
                    <OutlineChip label={t(`groupBilling.invoiceType.${inv.type}`)} />
                    <BillingStatusChip status={inv.status} />
                    <OutlineChip label={t(`groupBilling.collection.${inv.collectionMethod}`)} />
                  </Box>
                  <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: 18 }}>{formatMoney(inv.total, currency)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mt: 1 }}>
                  <Button size="small" sx={partnerLinkSx} onClick={() => onDownloadPdf(inv)}>{t('groupBilling.account.pdf')}</Button>
                  {inv.status !== 'void' && (
                    <>
                      <Button size="small" sx={partnerLinkSx} onClick={() => act('post', `/invoices/${inv.id}/send`, {}, t('groupBilling.notices.sent'))}>
                        {t('groupBilling.account.resend')}
                      </Button>
                      {inv.type === 'invoice' && (
                        <Button size="small" sx={partnerLinkSx} onClick={() => onSendInvoice('reissue', inv.id)} data-testid={`group-invoice-reissue-${inv.id}`}>
                          {t('groupBilling.account.reissue')}
                        </Button>
                      )}
                      <Button
                        size="small"
                        sx={partnerDangerLinkSx}
                        data-testid={`group-invoice-void-${inv.id}`}
                        onClick={async () => {
                          if (await confirm(t('groupBilling.account.voidConfirm', { number: inv.number }))) {
                            act('post', `/invoices/${inv.id}/void`, {}, t('groupBilling.notices.voided'));
                          }
                        }}
                      >
                        {t('groupBilling.account.void')}
                      </Button>
                    </>
                  )}
                </Box>
                {inv.installments.map((inst) => (
                  <Box key={inst.id} sx={{ borderTop: `1px solid ${DOCUMENT.rule}`, mt: 1, pt: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                      <Typography sx={{ fontSize: 13 }}>
                        {inst.label} · {inst.dueDate} · {formatMoney(inst.amount, currency)}
                        {inst.paid > 0 ? ` · ${t('groupBilling.account.paidAmount', { amount: formatMoney(inst.paid, currency) })}` : ''}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        {inst.clientReportedAt && inst.status !== 'paid' && (
                          <OutlineChip label={t('groupBilling.account.clientReported')} testId={`group-installment-reported-${inst.id}`} />
                        )}
                        <BillingStatusChip status={inst.overdue ? 'overdue' : inst.status} />
                        {inv.status !== 'void' && inst.status !== 'paid' && (
                          <>
                            <Button size="small" sx={partnerLinkSx} onClick={() => onRecordPayment(inst.id)}>
                              {t('groupBilling.account.markPaid')}
                            </Button>
                            <Button size="small" sx={partnerLinkSx} onClick={() => setEditingInstallment(inst.id)} data-testid={`group-installment-edit-${inst.id}`}>
                              {inst.overrideMethod ? t('groupBilling.account.changeLink') : t('groupBilling.account.overrideLink')}
                            </Button>
                          </>
                        )}
                      </Box>
                    </Box>
                    {editingInstallment === inst.id && (
                      <InstallmentOverride
                        installment={inst}
                        onCancel={() => setEditingInstallment(null)}
                        onSave={async (body) => {
                          const res = await act('put', `/installments/${inst.id}`, body, t('groupBilling.notices.saved'));
                          if (!res?.error) setEditingInstallment(null);
                        }}
                      />
                    )}
                  </Box>
                ))}
              </Box>
            ))}
          </Box>

          <Box>
            <Typography sx={{ ...eyebrowSx, mb: 1 }}>{t('groupBilling.account.payments')}</Typography>
            {!account.payments.length && <Typography sx={{ fontSize: 13, color: DOCUMENT.ink3 }}>{t('groupBilling.account.noPayments')}</Typography>}
            {account.payments.map((p) => (
              <Box key={p.id} sx={{ py: 0.75, borderBottom: `1px solid ${DOCUMENT.rule}` }} data-testid={`group-payment-${p.id}`}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                  <Typography sx={{ fontSize: 13.5 }}>
                    {p.receivedAt} · {t(`groupBilling.paymentMethod.${p.method}`, { defaultValue: p.method })}
                    {p.reference ? ` · ${p.reference}` : ''}
                    {p.refunded > 0 ? ` · ${t('groupBilling.account.refunded', { amount: formatMoney(p.refunded, currency) })}` : ''}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Typography sx={{ fontVariantNumeric: 'tabular-nums' }}>{formatMoney(p.amount, currency)}</Typography>
                    {p.amount - p.refunded > 0 && (
                      <Button size="small" sx={partnerLinkSx} onClick={() => setRefund({ id: p.id, amount: String(p.amount - p.refunded) })} data-testid={`group-payment-refund-${p.id}`}>
                        {t('groupBilling.account.refund')}
                      </Button>
                    )}
                    {!p.stripe && (
                      <Button
                        size="small"
                        sx={partnerDangerLinkSx}
                        onClick={async () => {
                          if (await confirm(t('groupBilling.account.deletePaymentConfirm'))) {
                            act('delete', `/payments/${p.id}`, undefined, t('groupBilling.notices.deleted'));
                          }
                        }}
                      >
                        {t('common.delete')}
                      </Button>
                    )}
                  </Box>
                </Box>
                {refund?.id === p.id && (
                  <Box sx={{ display: 'flex', gap: 1, mt: 1, alignItems: 'center' }}>
                    <TextField size="small" type="number" sx={{ ...fieldSx, width: 160 }} value={refund.amount}
                      onChange={(e) => setRefund({ ...refund, amount: e.target.value })}
                      inputProps={{ 'data-testid': `group-refund-amount-${p.id}` }} />
                    <Button size="small" sx={partnerLinkSx} onClick={() => setRefund(null)}>{t('common.cancel')}</Button>
                    <Button
                      size="small"
                      sx={partnerDangerLinkSx}
                      data-testid={`group-refund-confirm-${p.id}`}
                      onClick={async () => {
                        const msg = p.stripe ? t('groupBilling.account.refundStripeConfirm') : t('groupBilling.account.refundConfirm');
                        if (!(await confirm(msg))) return;
                        const res = await act('post', `/payments/${p.id}/refund`, { amount: Number(refund.amount) }, t('groupBilling.notices.refunded'));
                        if (!res?.error) setRefund(null);
                      }}
                    >
                      {t('groupBilling.account.refund')}
                    </Button>
                  </Box>
                )}
              </Box>
            ))}
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: 2.5, borderTop: `1px solid ${DOCUMENT.rule}` }}>
        <Button onClick={onClose} sx={partnerLinkSx}>{t('common.close')}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default GroupAccountDialog;
